import { useState, useEffect, useRef } from 'react'
import { menuAPI } from '../services/api'
import { XMarkIcon, PhotoIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { t } from '../i18n'

const MAX_PHOTOS = 8
const TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']

// Extra product photos, shown with the main photo as a swipeable carousel on
// the product page. For a saved product they upload straight away; while
// creating a new one they are queued (onPendingChange) and the form uploads
// them right after the product is created.
export default function ProductGalleryManager({ itemId = null, initialImages = [], onPendingChange }) {
  const [images, setImages] = useState(initialImages)
  const [pending, setPending] = useState([]) // { id, file, url }
  const [uploading, setUploading] = useState(false)
  const [dragging, setDragging] = useState(false)
  const fileInputRef = useRef(null)

  useEffect(() => { setImages(initialImages) }, [itemId])
  useEffect(() => { onPendingChange?.(pending.map(p => p.file)) }, [pending])
  useEffect(() => () => pending.forEach(p => URL.revokeObjectURL(p.url)), [])

  const count = images.length + pending.length

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => {
      if (!TYPES.includes(f.type)) { toast.error(t('{name} is not a supported image', { name: f.name })); return false }
      if (f.size > 5 * 1024 * 1024) { toast.error(t('{name} is larger than 5MB', { name: f.name })); return false }
      return true
    }).slice(0, Math.max(0, MAX_PHOTOS - count))
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (!files.length) {
      if (count >= MAX_PHOTOS) toast.error(t('You can add up to {count} photos', { count: MAX_PHOTOS }))
      return
    }
    if (!itemId) {
      setPending(prev => [...prev, ...files.map(file => ({ id: `${file.name}-${file.size}-${Math.random()}`, file, url: URL.createObjectURL(file) }))])
      return
    }
    setUploading(true)
    try {
      const { data } = await menuAPI.addItemImages(itemId, files)
      setImages(data.images)
      toast.success(t('Photos added'))
    } catch (error) {
      toast.error(t(error.response?.data?.error || 'Failed to upload photos'))
    } finally {
      setUploading(false)
    }
  }

  const remove = async (image) => {
    if (image.file) {
      URL.revokeObjectURL(image.url)
      setPending(prev => prev.filter(p => p.id !== image.id))
      return
    }
    try {
      const { data } = await menuAPI.deleteItemImage(itemId, image.id)
      setImages(data.images)
    } catch {
      toast.error(t('Failed to remove photo'))
    }
  }

  const tiles = [...images, ...pending]

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between gap-2 mb-1">
        <label className="block text-sm font-medium text-gray-700">{t('Gallery photos')}</label>
        <span className="text-xs text-gray-500">{count}/{MAX_PHOTOS}</span>
      </div>
      <p className="text-xs text-gray-500 mb-3">{t('Customers swipe through these after the main photo on the product page.')}</p>

      <div
        onDragOver={e => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files) }}
        className={`grid grid-cols-3 sm:grid-cols-5 gap-3 rounded-lg p-1 ${dragging ? 'ring-2 ring-blue-400 bg-blue-50' : ''}`}
      >
        {tiles.map((image, i) => (
          <div key={image.id} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 group bg-gray-50">
            <img src={image.url} alt={t('Photo {n}', { n: i + 1 })} className="w-full h-full object-cover" />
            {image.file && <span className="absolute bottom-1 start-1 text-[10px] font-semibold bg-white/90 text-gray-700 px-1.5 py-0.5 rounded">{t('New')}</span>}
            <button type="button" onClick={() => remove(image)} aria-label={t('Remove photo')}
              className="absolute top-1 end-1 bg-black/60 text-white rounded-full p-1 sm:opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity">
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {count < MAX_PHOTOS && (
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
            className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-1 text-gray-500 hover:border-blue-400 hover:text-blue-600 disabled:opacity-50">
            <PhotoIcon className="w-6 h-6" />
            <span className="text-[11px] font-medium">{uploading ? t('Uploading...') : t('Add photos')}</span>
          </button>
        )}
      </div>
      <input ref={fileInputRef} type="file" accept={TYPES.join(',')} multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
      {!itemId && pending.length > 0 && <p className="text-xs text-gray-500 mt-2">{t('These photos are uploaded when you save the product.')}</p>}
    </div>
  )
}
